import * as React from 'react';
import {
    Space,
    Collapse, List,
    Tag
} from 'antd';

import { AppStateContext } from '../../state/AppProvider';
import { GroupedChatHistory } from './ChatHistoryList';

import './ChatHistory.css';

const { Panel } = Collapse;

interface ChatHistoryListItemCellProps {
    item?: Conversation;
    onSelect: (item: Conversation | null) => void;
}

interface ChatHistoryListItemGroupsProps {
    groupedChatHistory: GroupedChatHistory[];
}

const formatMonth = (month: string) => {
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();

    const [monthName, yearString] = month.split(' ');
    const year = parseInt(yearString);

    if (year === currentYear) {
        return monthName;
    } else {
        return month;
    }
};

export const ChatHistoryListItemCell: React.FC<ChatHistoryListItemCellProps> = ({
    item,
    onSelect,
}) => {
    const appStateContext = React.useContext(AppStateContext)
    const isSelected = item?.id === appStateContext?.state.currentChat?.id;

    if (!item) {
        return null;
    }

    const handleSelectItem = () => {
        onSelect(item)

        appStateContext?.dispatch({ type: 'UPDATE_CURRENT_CHAT', payload: item })

    }

    function formatDateTime(dateString: string) {
        const date = new Date(dateString);

        //Format as d mmm yyyy hh:mm AM/PM
        const formattedDate = date.toLocaleString("en-SG", {
            day: "numeric",
            month: "short",
            year: "numeric",
            hour: "numeric",
            minute: "2-digit",
            hour12: true, // 12-hour format with AM/PM
        }).replace(",", " |")

        return formattedDate;
    }

    return (
        <div
            key={item.id}
            aria-label="recent activity item"
            onClick={handleSelectItem}
            onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && handleSelectItem()}
            style={{
                backgroundColor: isSelected ? '#e6e6e6' : 'transparent',
                padding: '10px',
                cursor: 'pointer',
            }}
            className={`br-16 ${isSelected ? 'selected-item bg-not-lightgreen' : ''}`}
        >
            <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                <div>
                    <div className='fs14-fw600 c-default'>{formatDateTime(item.createdAt)}</div>
                    <div className='fs14-fw400 c-default'>{item.title}</div>
                    <div className='fs14-fw400 c-default' style={{ display: item.agent_id ? 'block' : 'none' }}><Tag color="blue">{appStateContext?.state.agentOptions.find((agent) => agent.id === item.agent_id)?.name || item.agent_id}</Tag></div>
                </div>
            </Space>
        </div>
    );
};

export const ChatHistoryListItemGroups: React.FC<ChatHistoryListItemGroupsProps> = ({ groupedChatHistory }) => {
    const [, setSelectedItem] = React.useState<Conversation | null>(null);
    const appStateContext = React.useContext(AppStateContext)

    const handleSelectHistory = (item?: Conversation) => {
        if (item) {
            appStateContext!.state.currentChat = item
            setSelectedItem(item)
        }
    }

    const isItemVisible = (item: Conversation, categoryOptions: string[], startDate?: string, endDate?: string): boolean => {
        const categoryList: string[] = [];

        item.messages.forEach((message) => {
            message.classification?.category3?.forEach((category) => {
                if (!categoryList.includes(category)) {
                    categoryList.push(category);
                }
            });
        });

        const hasMatchingCategory = categoryOptions.length === 0 || categoryOptions.some((option) => categoryList.includes(option));
        if (!hasMatchingCategory) return false;

        const createdAt = new Date(item.createdAt || '');
        const endDateInclusive = endDate
            ? new Date(new Date(endDate).setDate(new Date(endDate).getDate() + 1))
            : undefined;

        const isWithinDateRange =
            (!startDate || createdAt >= new Date(startDate)) &&
            (!endDateInclusive || createdAt <= endDateInclusive);

        return isWithinDateRange;
    };

    const onRenderCell = (item?: Conversation) => {

        if (!item) return null;

        const categoryOptions = appStateContext?.state.chFilter.category_options || [];
        const startDate = appStateContext?.state.chFilter.date_filter.start_date;
        const endDate = appStateContext?.state.chFilter.date_filter.end_date;

        const visible = isItemVisible(item, categoryOptions, startDate ?? undefined, endDate ?? undefined);

        // console.log("Item visible:", visible, "createdAt:", item.createdAt);

        return visible ? (
            <ChatHistoryListItemCell item={item} onSelect={() => handleSelectHistory(item)} />
        ) : null;

    };

    return (
        <Collapse className={`${(appStateContext?.state?.screenSize ?? 0) < 3 ? 'br-0' : 'br-16'} b-primary overflow-hidden`} defaultActiveKey={groupedChatHistory.length ? [groupedChatHistory[0].month] : []}>
            {groupedChatHistory.map((group, index) => {

                const categoryOptions = appStateContext?.state.chFilter.category_options || [];
                const startDate = appStateContext?.state.chFilter.date_filter.start_date;
                const endDate = appStateContext?.state.chFilter.date_filter.end_date;

                const filteredEntries = group.entries.filter((entry) =>
                    isItemVisible(entry, categoryOptions, startDate ?? undefined, endDate ?? undefined)
                );

                if (filteredEntries.length === 0) {
                    return null;
                }

                return (
                    <Panel className={`${index == groupedChatHistory.length - 1 ? '' : 'b-bottom-primary'} c-default bg-root custom-panel-header`} header={<span className="c-default">{formatMonth(group.month)}</span>} key={group.month}>
                        <List dataSource={filteredEntries} renderItem={(item) => onRenderCell(item)} />
                    </Panel>
                );
            })}
        </Collapse>
    );
};